import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import { FaUser, FaGlobe, FaMapMarkerAlt, FaLightbulb, FaBuilding } from 'react-icons/fa';

const ViewStartup = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [startup, setStartup] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStartup = async () => {
            try {
                const res = await getRequest(`startups/${id}`);
                setStartup(res?.data);
            } catch (err) {
                console.error(err);
                toast.error("Failed to load startup details");
            } finally {
                setLoading(false);
            }
        };
        fetchStartup();
    }, [id]);

    if (loading) return <div className="text-center mt-5">Loading...</div>;
    if (!startup) return <div className="text-center mt-5">Startup not found</div>;

    const user = startup.user || {};

    return (
        <div className="container-fluid mt-5 mb-5">
            <div className="mainheadig mx-4 mb-4 d-flex justify-content-between align-items-center">
                <h4 className="text-white font-weight-bold">View Startup Profile</h4>
                <button className="btn btn-light font-weight-bold" onClick={() => navigate(-1)}>Back</button>
            </div>
            
            <div className="card mx-4 border-0 shadow-lg" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                {/* Banner Section */}
                <div style={{
                    height: '150px',
                    background: 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)',
                    position: 'relative'
                }}></div>

                <div className="card-body px-5 pb-5" style={{ position: 'relative', marginTop: '-75px' }}>
                    <div className="d-flex flex-column flex-md-row align-items-center align-items-md-end mb-4">
                        {/* Profile Image */}
                        <div style={{ 
                            width: '150px', 
                            height: '150px', 
                            borderRadius: '50%', 
                            border: '5px solid white',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                            backgroundColor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                            zIndex: 1
                        }}>
                            {startup.logo ? (
                                <img src={`${process.env.REACT_APP_BACKEND}uploads/${startup.logo}`} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <FaBuilding size={50} color="#ccc" />
                            )}
                        </div>
                        
                        {/* Title and Badges */}
                        <div className="ml-md-4 mt-3 mt-md-0 text-center text-md-left" style={{ flexGrow: 1 }}>
                            <h3 className="font-weight-bold mb-1" style={{ color: '#333' }}>{startup.startupName}</h3>
                            <div className="d-flex flex-wrap justify-content-center justify-content-md-start mb-2">
                                {startup.year && <span className="badge badge-warning mr-2 py-2 px-3 mt-2 text-white" style={{ fontSize: '13px', borderRadius: '20px', backgroundColor: '#f57c00' }}>Cohort: {startup.year}</span>}
                                {startup.fundingStatus && <span className="badge badge-info mr-2 py-2 px-3 mt-2" style={{ fontSize: '13px', borderRadius: '20px' }}>{startup.fundingStatus}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        {/* Left Column: Founder Details */}
                        <div className="col-lg-4 mb-4">
                            <div className="p-4" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px', height: '100%' }}>
                                <h5 className="font-weight-bold mb-4 border-bottom pb-2" style={{ color: '#0d47a1' }}>Founder Details</h5>
                                
                                <div className="d-flex align-items-center mb-3">
                                    <div className="icon-circle-small bg-primary text-white mr-3 d-flex justify-content-center align-items-center" style={{ width: '40px', height: '40px', borderRadius: '50%' }}>
                                        <FaUser />
                                    </div>
                                    <div>
                                        <div className="text-muted small">Founder</div>
                                        <div className="font-weight-bold">{`${user.firstName || ''} ${user.lastName || ''}`.trim()}</div>
                                    </div>
                                </div>


                                <div className="d-flex align-items-center mb-3">
                                    <div style={{ width: '35px', color: '#1976d2' }}><FaMapMarkerAlt size={18} /></div>
                                    <div>
                                        <div className="text-muted small">Incorporation State</div>
                                        <div className="font-weight-bold">{startup.state || 'N/A'}</div>
                                    </div>
                                </div>

                                {startup.year && (
                                    <div className="d-flex align-items-center mb-3">
                                        <div style={{ width: '35px', color: '#1976d2' }}><FaBuilding size={18} /></div>
                                        <div>
                                            <div className="text-muted small">Cohort Year</div>
                                            <div className="font-weight-bold">{startup.year}</div>
                                        </div>
                                    </div>
                                )}

                                {startup.website && (
                                    <div className="d-flex align-items-center mb-3">
                                        <div style={{ width: '35px', color: '#1976d2' }}><FaGlobe size={18} /></div>
                                        <div>
                                            <div className="text-muted small">Website</div>
                                            <a href={startup.website.startsWith('http') ? startup.website : `https://${startup.website}`} target="_blank" rel="noreferrer" className="font-weight-bold text-primary">
                                                {startup.website}
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Column: Startup Information */}
                        <div className="col-lg-8">
                            <div className="p-4" style={{ backgroundColor: '#ffffff', border: '1px solid #eee', borderRadius: '12px', height: '100%' }}>
                                <div className="mb-4">
                                    <h5 className="font-weight-bold mb-3 d-flex align-items-center" style={{ color: '#0d47a1' }}>
                                        <FaLightbulb className="mr-2" /> Idea Description
                                    </h5>
                                    <p className="text-secondary" style={{ lineHeight: '1.7', whiteSpace: 'pre-line' }}>
                                        {startup.description || 'No description provided.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewStartup;
